import { provide, inject, type InjectionKey } from 'vue'

import { getSupplierRegistTemplateMetaList } from '@/composables/api/customer/SupplierRegistTemplate'
import { useAuthStore } from '@/stores/authStore'
import { UserDisplayMode } from '@/constants/enums/UserDisplayMode'

type Store = {
  userPreferences: {
    [userId: string]: {
      userId: string
      // 初期表示用の値 ※ 主にログインやリロード時。ユーザーが変更可能。
      isMigratedInitialSupplierRegistTemplate: boolean // 出荷データ扱いの処理(migration)が必要なため、実行済み管理用フラグを用意: https://www.notion.so/asuene/BP-207d89fdd94680f797d7d096aed105a6
      initialSupplierRegistTemplateId: string | undefined // 初期表示用の値。ユーザーが変更可能。
    }
  }
  // 引き回し用の値 ※ 主に画面遷移時。システムが自動設定。
  selectedSupplierRegistTemplateId: string | undefined // 現状の各所の実装を鑑み null ではなく undefined で対応しておく
  selectedBuyerCreateSupplierRegistTemplateId: string | undefined // バイヤー作成質問グループ用
}
const storeKey = Symbol('browserStore') as InjectionKey<Ref<Store>>
const dataKey = {
  userPreferences: `asuene.esg.userPreference`,
  // userPreferences に移行する前の key 群
  userPreferenceOld1: 'asuene.esg.isMigratedInitialSupplierRegistTemplate',
  userPreferenceOld2: 'asuene.esg.initialSupplierRegistTemplateId',
  selectedSupplierRegistTemplateId: 'asuene.esg.selectedSupplierRegistTemplateId',
  selectedBuyerCreateSupplierRegistTemplateId: 'asuene.esg.selectedBuyerCreateSupplierRegistTemplateId',
} as const

// NOTE: getter ( try catch など、予期せぬエラーで動かなくなることを回避するための処理 )
const getUserPreferences = (): Store['userPreferences'] => {
  try {
    const json = localStorage.getItem(dataKey.userPreferences)
    if (!json) {
      return {}
    }
    return JSON.parse(json) as Store['userPreferences']
  }
  catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting user preferences:', error)
    return {}
  }
}

export const browserStoreProvide = () => {
  // NOTE: 初期化時に画面遷移時用の値はクリアすること、デフォルト値を優先する挙動を実現する
  sessionStorage.removeItem(dataKey.selectedSupplierRegistTemplateId)
  sessionStorage.removeItem(dataKey.selectedBuyerCreateSupplierRegistTemplateId)
  provide(storeKey, ref({
    userPreferences: getUserPreferences(),
    selectedSupplierRegistTemplateId: sessionStorage.getItem(dataKey.selectedSupplierRegistTemplateId) || undefined,
    selectedBuyerCreateSupplierRegistTemplateId: sessionStorage.getItem(dataKey.selectedBuyerCreateSupplierRegistTemplateId) || undefined,
  }))
}

const browserStoreInject = () => {
  const store = inject(storeKey)
  if (!store) {
    throw new Error('store is not provided')
  }
  return store
}

export const useBrowserStore = () => {
  const store = browserStoreInject()
  const authStore = useAuthStore()

  const userId = computed(() => {
    return authStore.user?.id || null
  })
  const userPreference = computed(() => {
    if (!userId.value) {
      return null
    }
    if (!store.value.userPreferences[userId.value]) {
      store.value.userPreferences[userId.value] = {
        userId: userId.value,
        isMigratedInitialSupplierRegistTemplate: false,
        initialSupplierRegistTemplateId: undefined,
      }
      localStorage.setItem(dataKey.userPreferences, JSON.stringify(store.value.userPreferences))
    }
    return store.value.userPreferences[userId.value]
  })

  const setSelectedSupplierRegistTemplateId = (supplierRegistTemplateId: string | undefined) => {
    store.value.selectedSupplierRegistTemplateId = supplierRegistTemplateId
    supplierRegistTemplateId
      ? sessionStorage.setItem(dataKey.selectedSupplierRegistTemplateId, supplierRegistTemplateId)
      : sessionStorage.removeItem(dataKey.selectedSupplierRegistTemplateId)

    /* 現状の URL のクエリパラメータにも反映したい場合には下記のコメントを外す */
    // const url = new URL(window.location.href)
    // const searchParams = url.searchParams
    // store.selectedSupplierRegistTemplateId
    //   ? searchParams.set('supplierRegistTemplateId', store.selectedSupplierRegistTemplateId)
    //   : searchParams.delete('supplierRegistTemplateId')
    // url.search = searchParams.toString()
    // window.history.replaceState({}, '', url.toString())
  }

  const getSelectedSupplierRegistTemplateId = () => {
    return store.value.selectedSupplierRegistTemplateId
  }
  const setInitialSupplierRegistTemplateId = (supplierRegistTemplateId: string | undefined) => {
    if (!userPreference.value) return
    userPreference.value.initialSupplierRegistTemplateId = supplierRegistTemplateId
    store.value.userPreferences[userPreference.value.userId] = userPreference.value
    localStorage.setItem(dataKey.userPreferences, JSON.stringify(store.value.userPreferences))
  }

  // NOTE: 旧 key (userPreferenceOld1, userPreferenceOld2) から userPreferences へ移行
  const doMigrateForPreferencePerUser = async () => {
    if (!userPreference.value) return
    if (userPreference.value.isMigratedInitialSupplierRegistTemplate) return
    if (authStore.userDisplayMode !== UserDisplayMode.BUYER) return
    // eslint-disable-next-line no-console
    console.log('[migration]: doMigrateForPreferencePerUser start')

    const old1 = localStorage.getItem(dataKey.userPreferenceOld1)
    const old2 = localStorage.getItem(dataKey.userPreferenceOld2)
    if (old1 !== null && old2 !== null) {
      userPreference.value.isMigratedInitialSupplierRegistTemplate = Boolean(old1)
      userPreference.value.initialSupplierRegistTemplateId = old2
      store.value.userPreferences[userPreference.value.userId] = userPreference.value
      localStorage.setItem(dataKey.userPreferences, JSON.stringify(store.value.userPreferences))
    }
    localStorage.removeItem(dataKey.userPreferenceOld1)
    localStorage.removeItem(dataKey.userPreferenceOld2)
    // eslint-disable-next-line no-console
    console.log('[migration]: doMigrateForPreferencePerUser end')
  }

  // NOTE: 初期値をセット
  const doMigrateInitialSupplierRegistTemplate = async () => {
    if (!userPreference.value) return
    if (userPreference.value.isMigratedInitialSupplierRegistTemplate) return
    if (authStore.userDisplayMode !== UserDisplayMode.BUYER) return
    // eslint-disable-next-line no-console
    console.log('[migration]: doMigrateInitialSupplierRegistTemplate start')
    // NOTE: ここでは、、
    //   下記の絞り込みはしない
    //     - filterIsNotBuyerEvaluate (isBuyerEvaluate)
    //     - getPublicSupplierRegistTemplateMetaList (isPublicImprovementPlan)
    //   該当データがあってもなくても migration 実行済みとする
    const response = await getSupplierRegistTemplateMetaList() // filterIsNotBuyerEvaluate は指定しない
    const latest = [...response.data.value || []].sort((a, b) => new Date(b.expiresDate).getTime() - new Date(a.expiresDate).getTime())[0]
    setInitialSupplierRegistTemplateId(latest?.id)
    userPreference.value.isMigratedInitialSupplierRegistTemplate = true
    store.value.userPreferences[userPreference.value.userId] = userPreference.value
    localStorage.setItem(dataKey.userPreferences, JSON.stringify(store.value.userPreferences))
    // eslint-disable-next-line no-console
    console.log('[migration]: doMigrateInitialSupplierRegistTemplate end')
  }

  const setSelectedBuyerCreateSupplierRegistTemplateId = (supplierRegistTemplateId: string | undefined) => {
    store.value.selectedBuyerCreateSupplierRegistTemplateId = supplierRegistTemplateId
    supplierRegistTemplateId
      ? sessionStorage.setItem(dataKey.selectedBuyerCreateSupplierRegistTemplateId, supplierRegistTemplateId)
      : sessionStorage.removeItem(dataKey.selectedBuyerCreateSupplierRegistTemplateId)
  }

  const getSelectedBuyerCreateSupplierRegistTemplateId = () => {
    return store.value.selectedBuyerCreateSupplierRegistTemplateId
  }

  return {
    value: store.value,
    userPreference: userPreference,
    doMigration: async () => {
      await doMigrateForPreferencePerUser()
      await doMigrateInitialSupplierRegistTemplate()
    },
    /** @deprecated リアクティブではないため、getSelectedSupplierRegistTemplateId をなるべく使用 */
    selectedSupplierRegistTemplateId: store.value.selectedSupplierRegistTemplateId,
    setSelectedSupplierRegistTemplateId,
    getSelectedSupplierRegistTemplateId,
    setInitialSupplierRegistTemplateId,
    setSelectedBuyerCreateSupplierRegistTemplateId,
    getSelectedBuyerCreateSupplierRegistTemplateId,
  } as const
}
